import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Credentials|Details|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Credential,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsCredentialDetails';
export type SPCatalogsCredentialDetailsLocNames =
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}ButtonEditDetails`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonUpdate`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelNameAbbr`
	| `${LocNameStart}LabelGroupName`
	| `${LocNameStart}LabelActiveDate`
	| `${LocNameStart}LabelSunsetDate`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}LabelGccn`
	| `${LocNameStart}LabelActivePeriod`
	| `${LocNameStart}ContentForever`
	| `${LocNameStart}LabelSuspendPeriod`
	| `${LocNameStart}LabelExtensionType`
	| `${LocNameStart}LabelPathType`
	| `${LocNameStart}LabelCadcExclude`
	| `${LocNameStart}ContentCadcTooltip`
	| `${LocNameStart}LabelHiddenFromCandidate`
	| `${LocNameStart}LabelRequirementCutoff`
	| `${LocNameStart}ContentNone`
	| `${LocNameStart}ContentAfterActiveDate`
	| `${LocNameStart}LabelRequirementMaxAge`
	| `${LocNameStart}ContentScavtaTooltip`
	| `${LocNameStart}LabelScavta`
	| `${LocNameStart}LabelTranscriptEnabled`
	| `${LocNameStart}LabelDisplayOrder`
	| `${LocNameStart}LabelCeFeePeriod`
	| `${LocNameStart}LabelUrl`
	| `${LocNameStart}LabelExternalId`
	| `${LocNameStart}LabelExamActiveAge`
	| `${LocNameStart}LabelReqActiveAge`
	| `${LocNameStart}LabelDescription`;

export const spCatalogsCredentialDetailsLocs: LocDefs<SPCatalogsCredentialDetailsLocNames> = {
	SPCatalogsCredentialDetailsTitleTab: {
		defaultValue: 'Credential Details',
		description: 'Tab Title',
		displayPath: `${pathStart}Tab Title`,
		displayType: 'Title',
		legacyName: 'Credential/CatalogsCredential.Details.Title.TabTitle',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsTitlePage: {
		defaultValue: 'Details',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Credential/CatalogsCredential.Details.Title.Details',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsButtonEditDetails: {
		defaultValue: 'Edit Details',
		description: 'Edit details button',
		displayPath: `${pathStart}Edit details`,
		displayType: 'Button',
		legacyName: 'Credential/CatalogsCredential.Details.Button.EditDetails',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Cancel button',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Credential/CatalogsCredential.Details.Button.Cancel',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsButtonUpdate: {
		defaultValue: 'Update',
		description: 'Update button',
		displayPath: `${pathStart}Update`,
		displayType: 'Button',
		legacyName: 'Credential/CatalogsCredential.Details.Button.Update',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelName: {
		defaultValue: 'Name',
		description: 'Label for: Name.',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.Name',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelNameAbbr: {
		defaultValue: 'Name Abbreviation',
		description: 'Label for: Name Abbreviation.',
		displayPath: `${pathStart}Name Abbreviation`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.NameAbbr',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelGroupName: {
		defaultValue: 'Group Name',
		description: 'Label for: Group Name.',
		displayPath: `${pathStart}Group Name`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.GroupName',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelActiveDate: {
		defaultValue: 'Active Date',
		description: 'Label for: Active Date.',
		displayPath: `${pathStart}Active Date`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.ActiveDate',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelSunsetDate: {
		defaultValue: 'Sunset Date',
		description: 'Label for: Sunset Date.',
		displayPath: `${pathStart}Sunset Date`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.SunsetDate',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelExpireDate: {
		defaultValue: 'Expire Date',
		description: 'Label for: Expire Date.',
		displayPath: `${pathStart}Expire Date`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.ExpireDate',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelGccn: {
		defaultValue: 'Generate Candidate Credential Number',
		description: 'Label for: Generate Candidate Credential Number.',
		displayPath: `${pathStart}Generate Candidate Credential Number`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.GCCN',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelActivePeriod: {
		defaultValue: 'Active Period',
		description: 'Label for: Active Period.',
		displayPath: `${pathStart}Active Period`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.ActivePeriod',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsContentForever: {
		defaultValue: 'Forever',
		description: 'Text showing Active Period is forever.',
		displayPath: `${pathStart}Forever`,
		displayType: 'Content',
		legacyName: 'Credential/CatalogsCredential.Details.Content.Forever',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelSuspendPeriod: {
		defaultValue: 'Suspend Period',
		description: 'Label for: Suspend Period.',
		displayPath: `${pathStart}Suspend Period`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.SuspendPeriod',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelExtensionType: {
		defaultValue: 'Extension Type',
		description: 'Label for: Extension Type.',
		displayPath: `${pathStart}Extension Type`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.ExtensionType',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelPathType: {
		defaultValue: 'Path Type',
		description: 'Label for: Path Type.',
		displayPath: `${pathStart}Path Type`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.PathType',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelCadcExclude: {
		defaultValue: 'CADC Exclude',
		description: 'Label for: CADC Exclude.',
		displayPath: `${pathStart}CADC Exclude`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.CADCExclude',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsContentCadcTooltip: {
		defaultValue: 'Credential Active Date Calculation Exclude',
		description: 'Abbreviation definition: Credential Active Date Calculation Exclude.',
		displayPath: `${pathStart}CADC Abbreviation definition`,
		displayType: 'Content',
		legacyName: 'Credential/CatalogsCredential.Details.Content.CadcTooltip',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelHiddenFromCandidate: {
		defaultValue: 'Hidden From Candidate',
		description: 'Label for: Hidden From Candidate.',
		displayPath: `${pathStart}Hidden From Candidate`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.HiddenFromCandidate',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelRequirementCutoff: {
		defaultValue: 'Requirement Cutoff',
		description: 'Label for: Requirement Cutoff.',
		displayPath: `${pathStart}Requirement Cutoff`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.RequirementCutoff',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsContentNone: {
		defaultValue: 'None',
		description: 'Text shown when there is no requirement cutoff.',
		displayPath: `${pathStart}None`,
		displayType: 'Content',
		legacyName: 'Credential/CatalogsCredential.Details.Content.None',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsContentAfterActiveDate: {
		defaultValue: 'On or After Active Date',
		description: 'Text shown when the requirement cutoff is On or After Active Date.',
		displayPath: `${pathStart}On or After Active Date`,
		displayType: 'Content',
		legacyName: 'Credential/CatalogsCredential.Details.Content.AfterActiveDate',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelRequirementMaxAge: {
		defaultValue: 'Requirement Max Age',
		description: 'Label for: Requirement Max Age.',
		displayPath: `${pathStart}Requirement Max Age`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.RequirementMaxAge',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsContentScavtaTooltip: {
		defaultValue: 'Suspended Credentials Are Valid Toward Achievement',
		description: 'Abbreviation definition: Suspended Credentials Are Valid Toward Achievement.',
		displayPath: `${pathStart}SCAVTA Tooltip`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.Content.ScavtaTooltip',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelScavta: {
		defaultValue: 'SCAVTA',
		description: 'Label for: SCAVTA.',
		displayPath: `${pathStart}SCAVTA`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.SCAVTA',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelTranscriptEnabled: {
		defaultValue: 'Transcript Enabled',
		description: 'Label for: Transcript Enabled.',
		displayPath: `${pathStart}Transcript Enabled`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.TranscriptEnabled',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelDisplayOrder: {
		defaultValue: 'Display Order',
		description: 'Label for: Display Order.',
		displayPath: `${pathStart}Display Order`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.DisplayOrder',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelCeFeePeriod: {
		defaultValue: 'Continuing Education Fee Period',
		description: 'Label for: Continuing Education Fee Period.',
		displayPath: `${pathStart}Continuing Education Fee Period`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.CEFeePeriod',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelUrl: {
		defaultValue: 'URL',
		description: 'Label for: URL.',
		displayPath: `${pathStart}URL`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.Url',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelExternalId: {
		defaultValue: 'External ID',
		description: 'Label for: External ID.',
		displayPath: `${pathStart}External ID`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.ExternalId',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelDescription: {
		defaultValue: 'Description',
		description: 'Label for: Description.',
		displayPath: `${pathStart}Description`,
		displayType: 'Label',
		legacyName: 'Credential/CatalogsCredential.Details.FieldLabel.Description',
		...sharedProperties,
	},
	SPCatalogsCredentialDetailsLabelExamActiveAge: {
		defaultValue: 'Exam Active Age',
		description: 'Label for: Exam Active Age.',
		displayPath: `${pathStart}Exam Active Age`,
		displayType: 'Label',
		shared: false,
		htmlAllowed: false,
		portal: 'SP',
		feature: HubFeature.CredentialContinuingEducationCiscoDisplayExamActiveAge,
	},
	SPCatalogsCredentialDetailsLabelReqActiveAge: {
		defaultValue: 'Requirement Active Age',
		description: 'Label for: Requirement Active Age.',
		displayPath: `${pathStart}Requirement Active Age`,
		displayType: 'Label',
		shared: false,
		htmlAllowed: false,
		portal: 'SP',
		feature: HubFeature.CredentialContinuingEducationCiscoDisplayExamActiveAge,
	},
};

export type SPCatalogsCredentialDetailsLocType = LocMap<SPCatalogsCredentialDetailsLocNames>;
